import { useMemo } from "react";

import { endOfMonth, isWithinInterval } from "date-fns/esm";

import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";

import RecapRange, { recapRangeIntervalMap } from "@app/enum/preview/RecapRange";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

// group by date (in numeric representation) and then by activity type
type ActivitiesGroupsMap = Record<number, Record<ActivityType["id"], Activity[]>>;

// array of [activityType, groups] tuples where groups is an array of [Date, Activity[]] tuples,
// i.e. activities grouped by date
type ActivitiesGroups = Array<Readonly<[Date, Array<Readonly<[ActivityType, Activity[]]>>]>>;

// Returns the closest date for the given date. It's different from date-fns'
// closestTo (https://date-fns.org/v2.16.1/docs/closestTo) in three things.
// First, it requires dateArray to be sorted.
// Second, it never selects date from dateArray if dateToCompare is out of dateArray range (i.e.
// is before the smallest date in dateArray or is after the biggest date of dateArray).
// Third difference is easier to demonstrate on an example. Say, we have this array of dates:
// A X B C
// where A,B,C - are dates from dateArray and X is dateToCompare
// Our closestDate would always pick date A.
// Whereas closestTo could also choose date B if X's time is closer to B than to A.
// We could eliminate this problem if we apply startOfDay for each date, but this is not appropriate
// since we can have date ranges with different granularity: hour, day, week, month.
const closestDate = (dateToCompare: Date, dateArray: Date[]): Date | undefined => {
  for (let i = 1; i < dateArray.length; i++) {
    if (isWithinInterval(dateToCompare, { end: dateArray[i], start: dateArray[i - 1] })) {
      return dateArray[i - 1];
    }
  }

  return undefined;
};

const useActivitiesGroups = (
  activities: Activity[],
  activityTypes: ActivityType[],
  xAxisDates: Date[] | undefined,
  recapRange: RecapRange
): ActivitiesGroups =>
  useMemo(() => {
    const activityTypeById = new Map(
      activityTypes.map((activityType) => [activityType.id, activityType])
    );

    // iterate over each activity type, then over each date
    const initialActivityGroups = xAxisDates?.reduce<ActivitiesGroupsMap>(
      (result, date) =>
        Object.assign(result, {
          [date.valueOf()]: activityTypes.reduce(
            (result, activityType) => Object.assign(result, { [activityType.id]: [] }),
            {}
          ),
        }),
      {}
    );

    // add one more - closing date to the xAxisDates array for correct closestDate work
    const dates: Date[] | undefined = xAxisDates
      ? recapRange === RecapRange.ALL_TIME
        ? [...xAxisDates, endOfMonth(Date.now())]
        : [...xAxisDates, recapRangeIntervalMap[recapRange]?.end as Date]
      : undefined;

    const activityGroups = activities
      .filter((activity) => activity.startAt)
      .reduce<ActivitiesGroupsMap | undefined>((result, activity) => {
        const activityDate = parseApiDate(activity.startAt!); // we ensure it's defined in a filter above
        if (!isValidDate(activityDate)) {
          return result;
        }

        // do not display activities which are from the hidden types
        if (!activityTypeById.has(activity.crmActivityType.id)) {
          return result;
        }

        const closestXAxisDate = dates ? closestDate(activityDate, dates) : undefined;
        if (!closestXAxisDate) {
          return result;
        }

        if (result && !result[closestXAxisDate.valueOf()][activity.crmActivityType.id]) {
          // this should not really happen because of the initial reduce value, but just in case
          result[closestXAxisDate.valueOf()][activity.crmActivityType.id] = [];
        }

        result?.[closestXAxisDate.valueOf()][activity.crmActivityType.id].push(activity);

        return result;
      }, initialActivityGroups);

    return Object.keys(activityGroups || {})
      .map((date: string) => {
        const activityTypeGroups = (activityGroups || {})[date as unknown as number];
        return [
          new Date(parseInt(date)),
          Object.keys(activityTypeGroups)
            .map(
              (activityTypeId: string) =>
                [
                  activityTypeById.get(parseInt(activityTypeId))!,
                  activityTypeGroups[activityTypeId as unknown as number],
                ] as const
            )
            // sort activity types by display order
            .sort(
              ([activityTypeA], [activityTypeB]) =>
                activityTypeA.displayOrder - activityTypeB.displayOrder
            ),
        ] as const;
      })
      .sort(([dateA], [dateB]) => dateA.valueOf() - dateB.valueOf());
  }, [activities, activityTypes, recapRange, xAxisDates]);

export default useActivitiesGroups;
